// apiClient — the ONLY way business modules should submit mutations.
// Handles online-direct, offline-enqueue, idempotency and optimistic UI.
import axios from "axios";
import { API_BASE, getToken } from "@/lib/api";
import * as outbox from "./outbox";
import { drainQueue } from "./sync";

let _simulateOffline = false;
export function setSimulateOffline(v) { _simulateOffline = !!v; }
export function getSimulateOffline() { return _simulateOffline; }

export function isOnline() {
  if (_simulateOffline) return false;
  return typeof navigator !== "undefined" ? navigator.onLine : true;
}

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "idem-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}

/**
 * Submit a mutation. Returns:
 *   { ok: true, immediate: true, response, idempotency_key }  — hit the network
 *   { ok: true, immediate: false, idempotency_key }           — queued offline
 * Never rejects unless offlineCapable=false AND currently offline.
 */
export async function mutate({
  method = "POST",
  path,
  body,
  entity_type = null,
  offlineCapable = true,
  owner_user_id = null,
  headers = {},
}) {
  const idempotency_key = newIdempotencyKey();

  if (!isOnline()) {
    if (!offlineCapable) {
      throw new Error("Offline: this action requires connectivity.");
    }
    await outbox.enqueue({
      method, path, body, headers,
      entity_type, owner_user_id, idempotency_key,
    });
    return { ok: true, immediate: false, idempotency_key };
  }

  // Try direct network. If network fails (not a 4xx), enqueue.
  try {
    const token = getToken();
    const res = await axios({
      method,
      url: `${API_BASE}${path}`,
      data: body,
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "X-Idempotency-Key": idempotency_key,
      },
      timeout: 15000,
      validateStatus: () => true,
    });
    if (res.status >= 200 && res.status < 300) {
      return { ok: true, immediate: true, response: res.data, idempotency_key };
    }
    // 4xx we surface immediately — no queuing
    const detail = res.data?.error?.message || res.data?.detail || res.data || `HTTP ${res.status}`;
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.response = res;
    throw err;
  } catch (err) {
    // Network error (no response) → enqueue
    const isNetwork = !err.response;
    if (offlineCapable && isNetwork) {
      await outbox.enqueue({
        method, path, body, headers,
        entity_type, owner_user_id, idempotency_key,
      });
      // Kick a drain in background
      drainQueue().catch(() => {});
      return { ok: true, immediate: false, idempotency_key };
    }
    throw err;
  }
}

/**
 * Cached GET — served from IndexedDB when offline and path matches a master.
 */
export async function get(path, params = {}) {
  const token = getToken();
  if (isOnline()) {
    const res = await axios.get(`${API_BASE}${path}`, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return res.data;
  }
  // Offline: try masters cache
  const { getAll } = await import("./masters");
  const map = {
    "/retailers": "retailers", "/distributors": "distributors", "/beats": "beats",
    "/pjp": "pjp", "/products": "products", "/skus": "skus",
    "/pricing": "pricing", "/schemes": "schemes",
  };
  const store = map[path];
  if (store) {
    const items = await getAll(store);
    return { items, total: items.length, page: 1, page_size: items.length, offline: true };
  }
  const e = new Error(`Offline: no cached data for ${path}`);
  e.code = "OFFLINE_NOT_CACHED";
  throw e;
}
